/**
 * ChatContext
 * 
 * React Context for managing chat state with Firestore storage and Gemini AI.
 * Provides chat messages, sending functionality, and real-time updates.
 * Supports multiple applications: 'chat', 'contly', 'taxper'.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, addDoc, query, orderBy, onSnapshot, Timestamp, where, doc, updateDoc, deleteDoc, serverTimestamp, arrayUnion, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { sendMessage as sendToGemini, isGeminiAvailable } from '../services/gemini';

const ChatContext = createContext({});

/**
 * Custom hook to access ChatContext
 * @returns {Object} Chat context value
 */
export const useChat = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
};

/**
 * ChatProvider Component
 * 
 * Provides chat state and functions to all child components.
 * Manages chat sessions and messages within those sessions.
 */
export const ChatProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const [activeApp, setActiveApp] = useState('chat'); // 'chat', 'contly', 'taxper'

    // Session State
    const [sessions, setSessions] = useState([]);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [isSessionsLoading, setIsSessionsLoading] = useState(false);

    // Message State
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [streamingMessage, setStreamingMessage] = useState('');

    // --- Sessions Management ---

    // 1. Load Sessions for Active App
    useEffect(() => {
        if (!currentUser) {
            setSessions([]);
            return;
        }

        setIsSessionsLoading(true);
        const rootCollection = activeApp === 'chat' ? 'chats' : activeApp;
        const sessionsRef = collection(db, rootCollection, currentUser.uid, 'sessions');
        const q = query(
            sessionsRef,
            orderBy('updatedAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const loadedSessions = [];
            snapshot.forEach((doc) => {
                loadedSessions.push({
                    id: doc.id,
                    ...doc.data(),
                    createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || doc.data().createdAt,
                    updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || doc.data().updatedAt,
                });
            });
            setSessions(loadedSessions);
            setIsSessionsLoading(false);
        }, (err) => {
            console.error('Error loading sessions:', err);
            setIsSessionsLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser, activeApp]);

    // 1.5 Auto-select most recent session if none selected
    useEffect(() => {
        if (!isSessionsLoading && sessions.length > 0 && !currentSessionId) {
            console.log('[ChatContext] Auto-selecting most recent session:', sessions[0].id);
            setCurrentSessionId(sessions[0].id);
        }
    }, [sessions, currentSessionId, isSessionsLoading]);

    // 2. Derive Messages for Current Session from sessions state
    useEffect(() => {
        if (!currentSessionId || sessions.length === 0) {
            setMessages([]);
            return;
        }

        const currentSession = sessions.find(s => s.id === currentSessionId);
        if (currentSession && currentSession.messages) {
            // Convert Firestore timestamps in the messages array back to ISO strings if needed
            // (Though they might already be strings if we save them that way, 
            // but consistency with previous logic is better)
            setMessages(currentSession.messages.map(msg => ({
                ...msg,
                createdAt: msg.timestamp?.toDate?.()?.toISOString() || msg.timestamp // Convert to ISO string for local use
            })));
        } else {
            setMessages([]);
        }
    }, [currentSessionId, sessions]);

    // --- Actions ---

    const createNewSession = useCallback(async () => {
        if (!currentUser) return null;

        try {
            const rootCollection = activeApp === 'chat' ? 'chats' : activeApp;
            const sessionsRef = collection(db, rootCollection, currentUser.uid, 'sessions');
            const docRef = await addDoc(sessionsRef, {
                userId: currentUser.uid,
                app: activeApp,
                title: 'New Chat',
                messages: [], // Initialize messages array
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });

            setCurrentSessionId(docRef.id);
            return docRef.id;
        } catch (err) {
            console.error('[ChatContext] Error creating session:', err);
            if (err.code === 'permission-denied') {
                console.error('[ChatContext] PERMISSION DENIED: Check Firestore security rules.');
            }
            setError('Failed to create new chat: ' + err.message);
            return null;
        }
    }, [currentUser, activeApp]);

    const selectSession = useCallback((sessionId) => {
        setCurrentSessionId(sessionId);
    }, []);

    const deleteSession = useCallback(async (sessionId) => {
        if (!currentUser) return;
        try {
            const rootCollection = activeApp === 'chat' ? 'chats' : activeApp;
            await deleteDoc(doc(db, rootCollection, currentUser.uid, 'sessions', sessionId));
            if (currentSessionId === sessionId) {
                setCurrentSessionId(null);
            }
        } catch (err) {
            console.error('Error deleting session:', err);
        }
    }, [currentUser, currentSessionId, activeApp]);

    const generateTitle = useCallback(async (sessionId, firstMessage) => {
        if (!isGeminiAvailable()) return;

        try {
            // Generate a short title using Gemini
            const prompt = `Generate a very short, concise title (max 4-5 words) for a chat that starts with this message: "${firstMessage}". Return ONLY the title, no quotes.`;
            const title = await sendToGemini(prompt, []); // Empty history for title generation

            // Update session title
            const rootCollection = activeApp === 'chat' ? 'chats' : activeApp;
            const sessionRef = doc(db, rootCollection, currentUser.uid, 'sessions', sessionId);
            // Update session title using setDoc with merge to ensure doc existence
            await setDoc(sessionRef, {
                title: title.trim() || 'New Chat',
                updatedAt: serverTimestamp()
            }, { merge: true });
        } catch (err) {
            console.error('Error generating title:', err);
        }
    }, [currentUser, activeApp, isGeminiAvailable]);

    /**
     * Switch the active chat application
     * @param {string} appName - 'chat', 'contly', or 'taxper'
     */
    /**
     * Switch the active chat application
     * @param {string} appName - 'chat', 'contly', or 'taxper'
     */
    const switchApp = useCallback((appName) => {
        if (appName === activeApp) return; // Prevent unnecessary updates/loops

        if (['chat', 'contly', 'taxper'].includes(appName)) {
            setActiveApp(appName);
            setCurrentSessionId(null); // Deselect session when switching apps
        }
    }, [activeApp]);

    /**
     * Send a message, save to Firestore, and get AI response
     * @param {string} content - Message content
     * @param {string} [systemPrompt] - Optional system prompt/context for specific actions
     */
    /**
     * Send a message, save to Firestore, and get AI response
     * @param {string} content - Message content
     * @param {string} [systemPrompt] - Optional system prompt/context for specific actions
     * @param {Array} [attachments] - Optional attachments [{ mimeType: string, url: string, data: base64String }]
     */
    const sendMessage = useCallback(async (content, systemPrompt = null, attachments = []) => {
        console.log('[ChatContext] sendMessage called with:', content);
        if (!content.trim()) return;

        if (!currentUser) {
            console.error('[ChatContext] No current user');
            setError('You must be logged in to send messages');
            return;
        }

        // Capture current state to avoid issues if state changes during async flow
        const capturedApp = activeApp;
        const rootCollection = capturedApp === 'chat' ? 'chats' : capturedApp;

        setIsLoading(true);
        setError(null);

        try {
            // Ensure we have a session
            let sessionId = currentSessionId;
            let isNewSession = false;
            console.log('[ChatContext] Current sessionId:', sessionId);

            if (!sessionId) {
                console.log('[ChatContext] No currentSessionId, creating new session for:', capturedApp);
                sessionId = await createNewSession();
                isNewSession = true;
                console.log('[ChatContext] New session created with ID:', sessionId);
                if (!sessionId) throw new Error("Failed to create session");
            }

            const sessionRef = doc(db, rootCollection, currentUser.uid, 'sessions', sessionId);

            // Use setDoc with merge: true for the first update to ensure the document exists
            // This is safer than updateDoc which fails if the document hasn't propagated yet
            console.log('[ChatContext] Ensuring session document exists at:', sessionRef.path);
            await setDoc(sessionRef, {
                updatedAt: serverTimestamp(),
                userId: currentUser.uid,
                app: capturedApp
            }, { merge: true }).catch(e => console.error("Error ensuring session document:", e));

            // Generate title if new session or title is still "New Chat" (in background)
            const currentSession = sessions.find(s => s.id === sessionId);
            const isDefaultTitle = !currentSession || currentSession.title === 'New Chat';

            if (isNewSession || isDefaultTitle) {
                generateTitle(sessionId, content);
            }

            // Save user message to Firestore (append to messages array)
            console.log('[ChatContext] Adding user message to Firestore...');
            await updateDoc(sessionRef, {
                messages: arrayUnion({
                    id: Date.now().toString(), // Add simple ID for React keys
                    content: content.trim(),
                    role: 'user',
                    timestamp: Timestamp.now(),
                    attachments: attachments.map(att => ({
                        mimeType: att.mimeType,
                        url: att.url || null // Firestore does not support undefined
                    })) // Save URLs to Firestore, not base64 data
                })
            });
            console.log('[ChatContext] User message added.');

            // Get AI response from Gemini if available
            if (isGeminiAvailable()) {
                console.log('[ChatContext] Gemini is available, requesting response...');
                try {
                    // Prepare chat history for Gemini (only user and assistant messages)
                    const history = messages
                        .filter(msg => msg.role === 'user' || msg.role === 'assistant')
                        .map(msg => ({
                            role: msg.role,
                            content: msg.content,
                        }));

                    // Prepare content with optional system prompt
                    let messageToSend = content;
                    if (systemPrompt) {
                        // Prepend system prompt to the user message for context
                        messageToSend = `${systemPrompt}\n\nUser Request: ${content}`;
                    }

                    // Filter out unsupported attachments for Gemini (like docx)
                    // These should rely on extracted text in the prompt instead
                    const supportedAttachments = attachments.filter(att => {
                        const mime = att.mimeType?.toLowerCase() || '';
                        const isUnsupported = mime.includes('word') || mime.includes('officedocument') || mime.includes('docx');
                        if (isUnsupported) {
                            console.log('[ChatContext] Filtering out unsupported binary attachment for Gemini:', att.mimeType);
                        }
                        return !isUnsupported;
                    });

                    // Get AI response
                    console.log('[ChatContext] Sending to Gemini...', {
                        messageToSend,
                        historyLength: history.length,
                        totalAttachments: attachments.length,
                        sentAttachments: supportedAttachments.length
                    });
                    const aiResponse = await sendToGemini(messageToSend, history, supportedAttachments);
                    console.log('[ChatContext] Gemini Response Received:', aiResponse ? (aiResponse.substring(0, 50) + '...') : 'NULL/EMPTY');

                    if (!aiResponse) {
                        console.error('[ChatContext] AI Response is empty/null - this might cause "nothing happens" in UI');
                    }

                    // Save AI response to Firestore (append to messages array)
                    console.log('[ChatContext] Adding assistant message to Firestore...');
                    await updateDoc(sessionRef, {
                        messages: arrayUnion({
                            id: (Date.now() + 1).toString(),
                            content: aiResponse,
                            role: 'assistant',
                            timestamp: Timestamp.now(),
                        })
                    });
                } catch (aiError) {
                    console.error('Error getting AI response:', aiError);
                    await updateDoc(sessionRef, {
                        messages: arrayUnion({
                            id: (Date.now() + 2).toString(),
                            content: 'Sorry, I encountered an error processing your request. Please try again.',
                            role: 'assistant',
                            timestamp: Timestamp.now(),
                        })
                    });
                }
            } else {
                console.warn('[ChatContext] Gemini is NOT available.');
                await updateDoc(sessionRef, {
                    messages: arrayUnion({
                        id: (Date.now() + 3).toString(),
                        content: 'AI responses are currently unavailable. Please check configuration.',
                        role: 'assistant',
                        timestamp: Timestamp.now(),
                    })
                });
            }

        } catch (err) {
            console.error('[ChatContext] Error sending message:', err);
            console.error('[ChatContext] Error Details:', JSON.stringify(err, Object.getOwnPropertyNames(err)));
            if (err.code === 'permission-denied') {
                console.error('[ChatContext] PERMISSION DENIED: User does not have write access to this collection.');
            }
            setError(err.message || 'Failed to send message. Please try again.');
        } finally {
            setIsLoading(false);
            setStreamingMessage('');
        }
    }, [currentUser, currentSessionId, createNewSession, messages, generateTitle, isGeminiAvailable, activeApp]);

    /**
     * Clear all messages (optional - for future use)
     */
    const clearMessages = () => {
        setMessages([]);
        setError(null);
    };

    const value = {
        messages,
        sessions,            // New
        currentSessionId,    // New
        createNewSession,    // New
        selectSession,       // New
        deleteSession,       // New
        isLoading,
        error,
        streamingMessage,
        activeApp,
        switchApp,
        sendMessage,
        clearMessages,
        isGeminiAvailable: isGeminiAvailable(),
    };

    return (
        <ChatContext.Provider value={value}>
            {children}
        </ChatContext.Provider>
    );
};
